import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { BrandMark } from "../BrandMark";
import { ThemeToggle } from "../ThemeToggle";

type Props = {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  /** Card width: "sm" (default) for single-column forms, "md" for register. */
  width?: "sm" | "md";
};

// Deterministic spark field — tiny twinkling dots scattered around the card.
// Pure CSS (built-in `animate-pulse` with staggered delays); the global
// reduced-motion kill-switch freezes them.
const SPARKS: Array<{ top: string; left: string; size: number; delay: string }> = [
  { top: "12%", left: "14%", size: 3, delay: "0s" },
  { top: "22%", left: "78%", size: 2, delay: "0.6s" },
  { top: "31%", left: "8%", size: 2, delay: "1.4s" },
  { top: "18%", left: "55%", size: 2, delay: "2s" },
  { top: "42%", left: "90%", size: 3, delay: "0.9s" },
  { top: "58%", left: "5%", size: 2, delay: "1.8s" },
  { top: "64%", left: "82%", size: 2, delay: "0.3s" },
  { top: "76%", left: "16%", size: 3, delay: "1.1s" },
  { top: "84%", left: "68%", size: 2, delay: "2.4s" },
  { top: "70%", left: "44%", size: 2, delay: "1.6s" },
  { top: "9%", left: "38%", size: 2, delay: "2.8s" },
  { top: "88%", left: "32%", size: 2, delay: "0.4s" },
  { top: "50%", left: "70%", size: 2, delay: "2.2s" },
  { top: "36%", left: "28%", size: 2, delay: "3s" },
];

/** Shared shell for all auth pages: a centered form card over a decorative
 * background (layered accent glows, masked grid, twinkling sparks) — the same
 * composition at every viewport. Theme-aware (light/dark). */
export function AuthLayout({ title, subtitle, children, width = "sm" }: Props) {
  const { t } = useTranslation();

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-white font-sans text-slate-900 dark:bg-slate-950 dark:text-white">
      {/* Background — glows, grid, sparks. Glows are radial gradients, not
          live blur() — see the `.glow` utility note in index.css. */}
      <div className="pointer-events-none absolute left-1/2 top-0 h-[30rem] w-[50rem] -translate-x-1/2 -translate-y-1/2 rounded-full glow [--glow:theme(colors.blue.400/15%)] dark:[--glow:theme(colors.blue.600/25%)]" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 translate-y-1/3 rounded-full glow [--glow:theme(colors.violet.400/10%)] dark:[--glow:theme(colors.violet.600/15%)]" />
      <div className="pointer-events-none absolute -right-24 top-1/3 h-72 w-72 rounded-full glow [--glow:theme(colors.indigo.400/10%)] dark:[--glow:theme(colors.indigo.600/15%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[26rem] bg-grid-slate bg-grid-md opacity-35 [mask-image:linear-gradient(to_bottom,black,transparent)] dark:opacity-20" />
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        {SPARKS.map((s, i) => (
          <span
            key={i}
            className="absolute animate-pulse rounded-full bg-blue-500/30 dark:bg-blue-200/40"
            style={{
              top: s.top,
              left: s.left,
              width: s.size,
              height: s.size,
              animationDelay: s.delay,
              boxShadow: "0 0 6px 1px currentColor",
            }}
          />
        ))}
      </div>

      {/* Content — centered column, identical composition on every device */}
      <div className="relative mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-10 sm:px-0">
        <div className={`mx-auto w-full ${width === "md" ? "max-w-md" : "max-w-sm"}`}>
          <div className="mb-6 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <BrandMark />
              <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
                Adam
              </span>
            </Link>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-slate-300 transition-colors hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:ring-white/15 dark:hover:bg-white/5 dark:hover:text-white"
              >
                <ArrowLeft size={13} />
                {t("auth.layout.backHome")}
              </Link>
            </div>
          </div>

          {title && (
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
          )}

          <div className="mt-5 rounded-3xl bg-white/95 p-6 ring-1 ring-slate-200 shadow-xl shadow-slate-200/60 md:bg-white/90 md:backdrop-blur dark:bg-white/[0.03] dark:ring-white/10 dark:shadow-2xl dark:shadow-black/40 sm:p-7">
            {children}
          </div>

          <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
            {t("auth.layout.tagline")}
          </p>
        </div>
      </div>
    </div>
  );
}
